import { AnimatePresence, motion } from 'framer-motion'
import {
  AlertTriangle,
  Building,
  CheckCircle,
  Clock,
  DollarSign,
  Download,
  Edit,
  Eye,
  FileText,
  Filter,
  Globe,
  MoreHorizontal,
  RefreshCw,
  Search,
  Trash2,
  User,
  XCircle
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { KPIBadge, KPIMetrics } from './KPIMetrics'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './ui/dropdown-menu'
import { Input } from './ui/input'
import { Progress } from './ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'
import { Tabs, TabsList, TabsTrigger } from './ui/tabs'

const CaseManagement = ({ currentUser }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedType, setSelectedType] = useState('all')
  const [viewMode, setViewMode] = useState('table') // 'table', 'card'
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalCases: 0,
    activeCases: 0,
    completedCases: 0
  })

  // 權限檢查：是否有全局權限（可以看到所有用戶的案件）
  const hasGlobalPermission = () => {
    if (!currentUser) return false
    return ['super_admin', 'admin', 'developer'].includes(currentUser.role)
  }

  // 是否顯示負責人員欄位
  const shouldShowAttorneyField = hasGlobalPermission()

  // 案件操作相關狀態
  const [showCaseDetailModal, setShowCaseDetailModal] = useState(false)
  const [showEditCaseModal, setShowEditCaseModal] = useState(false)
  const [selectedCase, setSelectedCase] = useState(null)
  const [editCaseForm, setEditCaseForm] = useState({})
  const [formLoading, setFormLoading] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [caseToDelete, setCaseToDelete] = useState(null)

  // 批量選擇相關狀態
  const [selectedCaseIds, setSelectedCaseIds] = useState([])
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false)

  // 獲取案件數據
  const fetchCases = async () => {
    try {
      setLoading(true)

      // 並行獲取統計數據和案件列表
      const [statsResponse, casesResponse] = await Promise.all([
        fetch('/api/stats/dashboard'),
        fetch('/api/cases/')
      ])

      // 處理統計數據
      if (statsResponse.ok) {
        const statsData = await statsResponse.json()
        const data = statsData.data || statsData
        setStats({
          totalCases: data.totalCases || 0,
          activeCases: data.activeCases || 0,
          completedCases: data.completedCases || 0
        })
      }

      // 處理案件列表
      if (casesResponse.ok) {
        const casesData = await casesResponse.json()
        if (casesData.success) {
          setCases(casesData.data || [])
        } else {
          console.error('獲取案件列表失敗:', casesData.error)
          setCases([])
        }
      } else {
        console.error('案件API請求失敗:', casesResponse.status)
        setCases([])
      }

    } catch (error) {
      console.error('獲取案件數據失敗:', error)
      setCases([])
      setStats({
        totalCases: 0,
        activeCases: 0,
        completedCases: 0
      })
    } finally {
      setLoading(false)
    }
  }

  // 案件操作處理函數
  const handleViewCase = (caseItem) => {
    setSelectedCase(caseItem)
    setShowCaseDetailModal(true)
  }

  const handleEditCase = (caseItem) => {
    setSelectedCase(caseItem)
    setEditCaseForm({
      title: caseItem.title || '',
      client: caseItem.client || '',
      type: caseItem.type || 'patent',
      status: caseItem.status || 'pending',
      priority: caseItem.priority || 'normal',
      deadline: caseItem.deadline || '',
      description: caseItem.description || '',
      attorney: caseItem.attorney || ''
    })
    setShowEditCaseModal(true)
  }

  const handleEditCaseFormChange = (field, value) => {
    setEditCaseForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleUpdateCase = async () => {
    if (!selectedCase) return

    try {
      setFormLoading(true)

      const response = await fetch(`/api/cases/${selectedCase.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(editCaseForm)
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success) {
          alert('案件更新成功！')
          setShowEditCaseModal(false)
          setSelectedCase(null)
          setEditCaseForm({})
          fetchCases() // 重新載入案件列表
        } else {
          alert(`更新案件失敗: ${result.message || '未知錯誤'}`)
        }
      } else {
        const errorData = await response.json().catch(() => ({}))
        alert(`更新案件失敗: ${errorData.message || '服務器錯誤'}`)
      }
    } catch (error) {
      console.error('更新案件時發生錯誤:', error)
      alert('更新案件時發生錯誤，請稍後再試')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDownloadFiles = (caseItem) => {
    // 實現文件下載功能
    alert(`正在下載案件 ${caseItem.title} 的相關文件...`)
    // TODO: 實現實際的文件下載邏輯
  }

  const handleDeleteCase = (caseItem) => {
    setCaseToDelete(caseItem)
    setShowDeleteConfirm(true)
  }

  const confirmDeleteCase = async () => {
    if (!caseToDelete) return

    try {
      setFormLoading(true)

      const response = await fetch(`/api/cases/${caseToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success) {
          setShowDeleteConfirm(false)
          setCaseToDelete(null)
          fetchCases() // 重新載入案件列表
        } else {
          alert(`刪除案件失敗: ${result.message || '未知錯誤'}`)
        }
      } else {
        const errorData = await response.json().catch(() => ({}))
        alert(`刪除案件失敗: ${errorData.message || '服務器錯誤'}`)
      }
    } catch (error) {
      console.error('刪除案件時發生錯誤:', error)
      alert('刪除案件時發生錯誤，請稍後再試')
    } finally {
      setFormLoading(false)
    }
  }

  // 批量選擇相關函數
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = filteredCases.map(c => c.id)
      setSelectedCaseIds(allIds)
    } else {
      setSelectedCaseIds([])
    }
  }

  const handleSelectCase = (caseId, checked) => {
    if (checked) {
      setSelectedCaseIds(prev => [...prev, caseId])
    } else {
      setSelectedCaseIds(prev => prev.filter(id => id !== caseId))
    }
  }

  const handleBatchDelete = () => {
    if (selectedCaseIds.length === 0) {
      alert('請先選擇要刪除的案件')
      return
    }
    setShowBatchDeleteConfirm(true)
  }

  const confirmBatchDelete = async () => {
    if (selectedCaseIds.length === 0) return

    try {
      setFormLoading(true)

      // 並行刪除所有選中的案件
      const deletePromises = selectedCaseIds.map(caseId =>
        fetch(`/api/cases/${caseId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      )

      const results = await Promise.all(deletePromises)
      const successCount = results.filter(r => r.ok).length
      const failCount = results.length - successCount

      if (successCount > 0) {
        alert(`成功刪除 ${successCount} 個案件${failCount > 0 ? `，${failCount} 個失敗` : ''}`)
        setSelectedCaseIds([])
        setShowBatchDeleteConfirm(false)
        fetchCases() // 重新載入案件列表
      } else {
        alert('批量刪除失敗，請稍後再試')
      }
    } catch (error) {
      console.error('批量刪除案件時發生錯誤:', error)
      alert('批量刪除案件時發生錯誤，請稍後再試')
    } finally {
      setFormLoading(false)
    }
  }

  // 初始化數據
  useEffect(() => {
    fetchCases()
  }, [])

  const statusConfig = {
    'DRAFT': {
      label: '草稿',
      color: 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600',
      icon: Clock
    },
    'SUBMITTED': {
      label: '已提交',
      color: 'bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200 border border-blue-200 dark:border-blue-700',
      icon: Clock
    },
    'UNDER_REVIEW': {
      label: '審查中',
      color: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900/30 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-700',
      icon: AlertTriangle
    },
    'GRANTED': {
      label: '已核准',
      color: 'bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200 border border-green-200 dark:border-green-700',
      icon: CheckCircle
    },
    'REJECTED': {
      label: '已拒絕',
      color: 'bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200 border border-red-200 dark:border-red-700',
      icon: XCircle
    },
    'APPROVED': {
      label: '已批准',
      color: 'bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200 border border-green-200 dark:border-green-700',
      icon: CheckCircle
    },
    'EXPIRED': {
      label: '已過期',
      color: 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600',
      icon: XCircle
    },
    'ABANDONED': {
      label: '已放棄',
      color: 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600',
      icon: XCircle
    },
    // 舊版兼容
    'active': {
      label: '進行中',
      color: 'bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200 border border-blue-200 dark:border-blue-700',
      icon: Clock
    },
    'pending': {
      label: '待處理',
      color: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900/30 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-700',
      icon: AlertTriangle
    },
    'completed': {
      label: '已完成',
      color: 'bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200 border border-green-200 dark:border-green-700',
      icon: CheckCircle
    }
  }

  const getStatusConfig = (status) => {
    return statusConfig[status] || {
      label: status || '未知',
      color: 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600',
      icon: Clock
    }
  }

  const priorityConfig = {
    'high': { color: 'bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200 border border-red-200 dark:border-red-700' },
    'normal': { color: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900/30 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-700' },
    'low': { color: 'bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200 border border-green-200 dark:border-green-700' },
    // 舊的配置保留作為兼容
    'High': { color: 'bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200 border border-red-200 dark:border-red-700' },
    'Medium': { color: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900/30 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-700' },
    'Low': { color: 'bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200 border border-green-200 dark:border-green-700' }
  }

  const filteredCases = cases.filter(caseItem => {
    const searchLower = searchQuery.toLowerCase()
    const matchesSearch = !searchQuery ||
      (caseItem.title && caseItem.title.toLowerCase().includes(searchLower)) ||
      (caseItem.client && caseItem.client.toLowerCase().includes(searchLower)) ||
      (caseItem.caseNumber && caseItem.caseNumber.toLowerCase().includes(searchLower)) ||
      (caseItem.id && caseItem.id.toString().toLowerCase().includes(searchLower))

    const matchesStatus = selectedStatus === 'all' || caseItem.status === selectedStatus
    const matchesType = selectedType === 'all' || caseItem.type === selectedType

    return matchesSearch && matchesStatus && matchesType
  })

  const CaseCard = ({ caseItem }) => {
    const StatusIcon = statusConfig[caseItem.status]?.icon || Clock

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="patent-card-container bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-lg dark:hover:shadow-gray-900/50 transition-all duration-200"
      >
        <div className="patent-card-content">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 text-clamp-2">
                  {caseItem.title || '無標題'}
                </h3>
                <Badge className={priorityConfig[caseItem.priority]?.color || 'bg-gray-100 text-gray-800'}>
                  {caseItem.priority || '未設定'}
                </Badge>
              </div>
              <div className="flex items-center space-x-3 mb-3">
                <div className="bg-blue-50 dark:bg-blue-900 px-3 py-1 rounded">
                  <span className="text-xs font-bold text-blue-700 dark:text-blue-300">
                    {caseItem.caseNumber || caseItem.id || 'N/A'}
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                  ID: {caseItem.id}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 text-clamp-2 mb-4">
                {caseItem.description || '無描述'}
              </p>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 ml-2 relative z-50">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 z-[9999]" sideOffset={5}>
                <DropdownMenuItem onClick={() => handleViewCase(caseItem)}>
                  <Eye className="h-4 w-4 mr-2" />
                  查看詳情
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleEditCase(caseItem)}>
                  <Edit className="h-4 w-4 mr-2" />
                  編輯案件
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadFiles(caseItem)}>
                  <Download className="h-4 w-4 mr-2" />
                  下載文件
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="patent-info-grid mb-4 text-sm">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Building className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span className="text-gray-600 dark:text-gray-400">客戶:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100 text-truncate">
                  {caseItem.client || '未指定'}
                </span>
              </div>
              {shouldShowAttorneyField && (
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="text-gray-600 dark:text-gray-400">負責人員:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100 text-truncate">
                    {caseItem.attorney || '未分配'}
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Globe className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span className="text-gray-600 dark:text-gray-400">管轄區:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100 text-truncate">
                  {caseItem.jurisdiction || '未設定'}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <DollarSign className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span className="text-gray-600 dark:text-gray-400">案件價值:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  ${(caseItem.value || 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <StatusIcon className="h-4 w-4" />
                <Badge className={statusConfig[caseItem.status]?.color || 'bg-gray-100 text-gray-800'}>
                  {getStatusConfig(caseItem.status).label}
                </Badge>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">進度</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{caseItem.progress || 0}%</span>
              </div>
              <Progress value={caseItem.progress || 0} className="h-2" />
            </div>
          </div>
        </div>

        <div className="patent-actions px-6 pb-6">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>文件數: {caseItem.documents}</span>
            <span>最後更新: {caseItem.lastUpdate}</span>
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{'案件管理'}</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">{'管理所有專利申請案件的生命週期'}</p>
        </div>
      </motion.div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center space-y-3">
              <FileText className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{'總案件數'}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{loading ? '...' : stats.totalCases}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center space-y-3">
              <Clock className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{'進行中'}</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {loading ? '...' : stats.activeCases}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center space-y-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{'已完成'}</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {loading ? '...' : stats.completedCases}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 搜尋和篩選 */}
      <div className="mt-8 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:space-x-6 lg:items-center">
              {/* 搜尋框 */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="搜尋案件標題、客戶或案件編號..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-10"
                  />
                </div>
              </div>

              {/* 篩選選項 */}
              <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3 lg:flex-shrink-0">
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-full sm:w-36 h-10">
                    <SelectValue placeholder="所有狀態" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">所有狀態</SelectItem>
                    <SelectItem value="active">進行中</SelectItem>
                    <SelectItem value="pending">待處理</SelectItem>
                    <SelectItem value="completed">已完成</SelectItem>
                    <SelectItem value="closed">已關閉</SelectItem>
                    <SelectItem value="rejected">已拒絕</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-full sm:w-36 h-10">
                    <SelectValue placeholder="所有類型" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">所有類型</SelectItem>
                    <SelectItem value="invention">發明專利</SelectItem>
                    <SelectItem value="utility_model">新型專利</SelectItem>
                    <SelectItem value="design">設計專利</SelectItem>
                    <SelectItem value="trademark">商標</SelectItem>
                    <SelectItem value="copyright">著作權</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" className="w-full sm:w-auto h-10">
                  <Filter className="h-4 w-4 mr-2" />
                  進階篩選
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 案件列表 */}
      <div className="mt-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{'案件列表'}</CardTitle>
                <CardDescription>
                  顯示 {filteredCases.length} 個案件，共 {cases.length} 個
                  {selectedCaseIds.length > 0 && ` (已選擇 ${selectedCaseIds.length} 個)`}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {selectedCaseIds.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBatchDelete}
                    disabled={formLoading}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    批量刪除 ({selectedCaseIds.length})
                  </Button>
                )}
                <Tabs value={viewMode} onValueChange={setViewMode}>
                  <TabsList>
                    <TabsTrigger value="table">表格檢視</TabsTrigger>
                    <TabsTrigger value="card">卡片檢視</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-visible">
            <AnimatePresence mode="wait">
              {viewMode === 'table' ? (
                <motion.div
                  key="table"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="table-wrapper overflow-x-auto overflow-y-visible relative"
                  style={{ overflowY: 'visible' }}
                >
                  <Table className="case-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">
                          <input
                            type="checkbox"
                            checked={filteredCases.length > 0 && selectedCaseIds.length === filteredCases.length}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="cursor-pointer w-4 h-4"
                          />
                        </TableHead>
                        <TableHead className="min-w-[120px]">案件編號</TableHead>
                        <TableHead className="min-w-[200px]">案件</TableHead>
                        {shouldShowAttorneyField && (
                          <TableHead className="min-w-[150px]">負責人員</TableHead>
                        )}
                        <TableHead className="min-w-[100px]">狀態</TableHead>
                        <TableHead className="min-w-[100px]">進度</TableHead>
                        <TableHead className="min-w-[120px]">處理時間</TableHead>
                        <TableHead className="min-w-[100px] text-center">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCases.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={shouldShowAttorneyField ? 8 : 7} className="text-center py-12">
                            <div className="text-gray-500 dark:text-gray-400">
                              <p className="text-lg font-medium mb-2">暫無案件資料</p>
                              <p className="text-sm">您可以創建新案件開始管理</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredCases.map((caseItem, index) => {
                          // 簡化的渲染邏輯，減少可能的錯誤
                          return (
                            <TableRow key={caseItem.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <TableCell>
                                <input
                                  type="checkbox"
                                  checked={selectedCaseIds.includes(caseItem.id)}
                                  onChange={(e) => handleSelectCase(caseItem.id, e.target.checked)}
                                  className="cursor-pointer w-4 h-4"
                                />
                              </TableCell>
                              <TableCell className="font-mono">
                                <div className="bg-blue-50 dark:bg-blue-900 px-2 py-1 rounded text-center min-w-fit">
                                  <p className="text-xs font-bold text-blue-700 dark:text-blue-300">
                                    {caseItem.caseNumber || caseItem.id || 'N/A'}
                                  </p>
                                  <p className="text-xs text-blue-500 dark:text-blue-400">
                                    ID: {caseItem.id}
                                  </p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="max-w-[200px]">
                                  <p className="font-medium text-gray-900 dark:text-gray-100 text-truncate" title={caseItem.title || '無標題'}>
                                    {caseItem.title || '無標題'}
                                  </p>
                                </div>
                              </TableCell>
                              {shouldShowAttorneyField && (
                                <TableCell>
                                  <p className="text-sm text-gray-900 dark:text-gray-100 text-truncate" title={caseItem.attorney || '未分配'}>
                                    {caseItem.attorney || '未分配'}
                                  </p>
                                </TableCell>
                              )}
                              <TableCell>
                                <Badge className={statusConfig[caseItem.status]?.color || 'bg-gray-100 text-gray-800'}>
                                  {getStatusConfig(caseItem.status).label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1 min-w-[80px]">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-900 dark:text-gray-100">{caseItem.progress || 0}%</span>
                                  </div>
                                  <Progress value={caseItem.progress || 0} className="h-2" />
                                </div>
                              </TableCell>
                              <TableCell>
                                <KPIBadge
                                  totalDuration={caseItem.total_duration}
                                  uploadDuration={caseItem.upload_duration}
                                  analysisDuration={caseItem.analysis_duration}
                                  searchDuration={caseItem.search_duration}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex justify-center relative z-50">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48 z-[9999]" sideOffset={5}>
                                      <DropdownMenuItem onClick={() => handleViewCase(caseItem)}>
                                        <Eye className="h-4 w-4 mr-2" />
                                        查看詳情
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleEditCase(caseItem)}>
                                        <Edit className="h-4 w-4 mr-2" />
                                        編輯案件
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleDownloadFiles(caseItem)}>
                                        <Download className="h-4 w-4 mr-2" />
                                        下載文件
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })
                      )}
                    </TableBody>
                  </Table>
                </motion.div>
              ) : (
                <motion.div
                  key="card"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="patent-results-grid"
                >
                  {filteredCases.length === 0 ? (
                    <div className="w-full text-center py-12">
                      <div className="text-gray-500 dark:text-gray-400">
                        <p className="text-lg font-medium mb-2">暫無案件</p>
                        <p className="text-sm">開始創建您的第一個案件</p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                      {filteredCases.map((caseItem) => (
                        <div key={caseItem.id || Math.random()} className="patent-card">
                          <CaseCard caseItem={caseItem} />
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>

        {/* 案件詳情模態對話框 */}
        {showCaseDetailModal && selectedCase && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    案件詳情
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowCaseDetailModal(false)
                      setSelectedCase(null)
                    }}
                  >
                    <XCircle className="h-5 w-5" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">案件標題</label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">{selectedCase.title || '無標題'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">客戶名稱</label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">{selectedCase.client || '未指定'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">案件類型</label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">{selectedCase.type || '未設定'}</p>
                    </div>
                    {shouldShowAttorneyField && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">負責人員</label>
                        <p className="text-gray-900 dark:text-gray-100 mt-1">{selectedCase.attorney || '未分配'}</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">案件狀態</label>
                      <div className="mt-1">
                        <Badge className={statusConfig[selectedCase.status]?.color || 'bg-gray-100 text-gray-800'}>
                          {getStatusConfig(selectedCase.status).label}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">進度</label>
                      <div className="mt-1">
                        <div className="flex items-center space-x-2">
                          <Progress value={selectedCase.progress || 0} className="flex-1" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{selectedCase.progress || 0}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedCase.description && (
                  <div className="mt-6">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">案件描述</label>
                    <p className="text-gray-900 dark:text-gray-100 mt-1">{selectedCase.description}</p>
                  </div>
                )}

                {/* KPI 效能指標 */}
                <KPIMetrics kpiData={selectedCase} />
              </div>
            </motion.div>
          </div>
        )}

        {/* 編輯案件模態對話框 */}
        {showEditCaseModal && selectedCase && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    編輯案件
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowEditCaseModal(false)
                      setSelectedCase(null)
                      setEditCaseForm({})
                    }}
                  >
                    <XCircle className="h-5 w-5" />
                  </Button>
                </div>

                <div className="space-y-4">
                  {/* 案件標題 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      案件標題 *
                    </label>
                    <Input
                      placeholder="請輸入案件標題"
                      value={editCaseForm.title}
                      onChange={(e) => handleEditCaseFormChange('title', e.target.value)}
                    />
                  </div>

                  {/* 客戶名稱 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      客戶名稱 *
                    </label>
                    <Input
                      placeholder="請輸入客戶名稱"
                      value={editCaseForm.client}
                      onChange={(e) => handleEditCaseFormChange('client', e.target.value)}
                    />
                  </div>

                  {/* 案件類型和狀態 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        案件類型
                      </label>
                      <Select
                        value={editCaseForm.type}
                        onValueChange={(value) => handleEditCaseFormChange('type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="patent">專利</SelectItem>
                          <SelectItem value="utility">新型專利</SelectItem>
                          <SelectItem value="design">設計專利</SelectItem>
                          <SelectItem value="trademark">商標</SelectItem>
                          <SelectItem value="copyright">著作權</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        案件狀態
                      </label>
                      <Select
                        value={editCaseForm.status}
                        onValueChange={(value) => handleEditCaseFormChange('status', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">待處理</SelectItem>
                          <SelectItem value="active">進行中</SelectItem>
                          <SelectItem value="completed">已完成</SelectItem>
                          <SelectItem value="rejected">已駁回</SelectItem>
                          <SelectItem value="expired">已過期</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* 負責人員 - 只有全局權限用戶才顯示 */}
                  {shouldShowAttorneyField && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        負責人員
                      </label>
                      <Input
                        placeholder="請輸入負責人員姓名"
                        value={editCaseForm.attorney}
                        onChange={(e) => handleEditCaseFormChange('attorney', e.target.value)}
                      />
                    </div>
                  )}

                  {/* 案件描述 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      案件描述
                    </label>
                    <textarea
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                      rows={4}
                      placeholder="請輸入案件描述"
                      value={editCaseForm.description}
                      onChange={(e) => handleEditCaseFormChange('description', e.target.value)}
                    />
                  </div>
                </div>

                {/* 按鈕區域 */}
                <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowEditCaseModal(false)
                      setSelectedCase(null)
                      setEditCaseForm({})
                    }}
                    disabled={formLoading}
                  >
                    取消
                  </Button>
                  <Button
                    onClick={handleUpdateCase}
                    disabled={formLoading || !editCaseForm.title?.trim() || !editCaseForm.client?.trim()}
                  >
                    {formLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        更新中...
                      </>
                    ) : (
                      <>
                        <Edit className="h-4 w-4 mr-2" />
                        更新案件
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* 刪除確認對話框 */}
        {showDeleteConfirm && caseToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4"
            >
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    確認刪除案件
                  </h3>
                </div>

                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  您確定要刪除案件「{caseToDelete.title}」嗎？此操作無法撤銷。
                </p>

                <div className="flex items-center justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(false)
                      setCaseToDelete(null)
                    }}
                    disabled={formLoading}
                  >
                    取消
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={confirmDeleteCase}
                    disabled={formLoading}
                  >
                    {formLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        刪除中...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        確認刪除
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* 批量刪除確認對話框 */}
        {showBatchDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4"
            >
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    確認批量刪除
                  </h3>
                </div>

                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  您確定要刪除選中的 <span className="font-bold text-red-600">{selectedCaseIds.length}</span> 個案件嗎？此操作無法撤銷。
                </p>

                <div className="flex items-center justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowBatchDeleteConfirm(false)}
                    disabled={formLoading}
                  >
                    取消
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={confirmBatchDelete}
                    disabled={formLoading}
                  >
                    {formLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        刪除中...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        確認批量刪除
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CaseManagement


